import {
  AfterViewChecked,
  AfterViewInit,
  Directive,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  Renderer2
} from '@angular/core';
import {CommerceConfig} from '@app/config/CommerceConfig';
import {BreakpointsConfig} from '@app/model/breakpoint.model';
import {Subscription} from 'rxjs';
import {take} from 'rxjs/operators';
import {MediaView} from '@app/model/media-view.model';
import {isPlatformBrowser} from '@angular/common';

@Directive({
  selector: '[dcpDropdownPosition]'
})
export class DropdownPositionDirective implements OnDestroy, AfterViewInit, AfterViewChecked {
  @Input('dcpDropdownPosition') componentRef: any;
  public dropdownMenuOpened: EventEmitter<boolean> = new EventEmitter<boolean>();
  public dropdownToggleAppear: EventEmitter<boolean> = new EventEmitter<boolean>();
  public headerRebuild: EventEmitter<boolean> = new EventEmitter<boolean>();

  public dropdownMenuSubscription: Subscription;
  public dropdownToggleSubscription: Subscription;
  public headerRebuildSubscription: Subscription;
  public dropdownStateChangedSubscription: Subscription;
  mediaView: MediaView;

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    public renderer: Renderer2
  ) {
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (this.componentRef.highlighted) {
      const windowWidthOnResize: number = (event.target as Window).innerWidth;
      this.checkMediaView(windowWidthOnResize);
      this.removeUserMenuTabletAdjustments();
      if (this.mediaView === MediaView.TabletView) {
        this.setUserMenuPositionTablet();
      }
      if (this.mediaView === MediaView.MobileView) {
        this.setUserMenuPositionMobile();
      }
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupDropdown();
      this.setupHeader();
    }
  }

  private setupDropdown(): void {
    // Subscribe to dropdownStateChanged EventEmitter when dropdown open or close
    this.dropdownStateChangedSubscription = this.componentRef.dropdownStateChanged.subscribe((event) => {
      const bodyOffsetWidth: number = document.body.offsetWidth;
      this.checkMediaView(bodyOffsetWidth);
      if (event) {
        if (this.mediaView === MediaView.MobileView) {
          this.setUserMenuPositionMobile();
        }
        if (this.mediaView === MediaView.TabletView) {
          // Should set user menu position on tablet only after user menu have been opened
          // There is no standard ngb output event, that dropdown menu have been opened
          // Check it via MutationObserver in couple with dropdownMenuOpened EventEmitter
          this.dropdownMenuSubscription = this.dropdownMenuOpened.subscribe((isOpen: boolean) => {
            if (isOpen) {
              this.setUserMenuPositionTablet();
            }
          });
        }
      } else {
        this.removeUserMenuTabletAdjustments();
      }
    });
  }

  private setupHeader(): void {
    const headerEl: HTMLElement = document.querySelector('.header');
    this.dropdownToggleSubscription = this.dropdownToggleAppear
      .pipe(take(1))
      .subscribe(() => {
        this.dropdownMenuMutationObserve();
      });
    if (headerEl) {
      this.userHeaderMutationObserve();
    }
    this.headerRebuildSubscription = this.headerRebuild.subscribe((event: boolean) => {
      if (event && this.componentRef.ngbDropdown) {
        if (this.mediaView === MediaView.MobileView) {
          this.setUserMenuPositionMobile();
        }
        if (this.mediaView === MediaView.TabletView) {
          this.setUserMenuPositionTablet();
        }
      }
    });

  }

  ngAfterViewChecked(): void {
    if (this.componentRef && this.componentRef.userMenuDropdown && !this.componentRef.userMenuDropdown.nativeElement.classList.contains('ngbdropdown-mobile-adjustment')) {
      this.renderer.addClass(this.componentRef.userMenuDropdown.nativeElement, 'ngbdropdown-mobile-adjustment');
    }
    // After login dropdown toggle appear only after view checked
    if (this.componentRef.dropdownToggle) {
      this.dropdownToggleAppear.emit(true);
    }
  }

  ngOnDestroy(): void {
    if (this.dropdownToggleSubscription && !this.dropdownToggleSubscription.closed) {
      this.dropdownToggleSubscription.unsubscribe();
    }
    if (this.headerRebuildSubscription && !this.headerRebuildSubscription.closed) {
      this.headerRebuildSubscription.unsubscribe();
    }
    if (this.dropdownStateChangedSubscription && !this.dropdownStateChangedSubscription.closed) {
      this.dropdownStateChangedSubscription.unsubscribe();
    }
  }

  setUserMenuPositionMobile(): void {
    if (isPlatformBrowser(this.platformId)) {
      const bodyWidth: number = document.body.offsetWidth;
      const ngbDropdownRightPosition: number = this.componentRef.ngbDropdown.nativeElement.getBoundingClientRect().right - bodyWidth;
      this.componentRef.userMenuDropdown.nativeElement.style.setProperty('--width', bodyWidth + 'px');
      this.componentRef.userMenuDropdown.nativeElement.style.setProperty('--right-position', ngbDropdownRightPosition + 'px');
    }
  }

  setUserMenuPositionTablet(): void {
    if (isPlatformBrowser(this.platformId)) {
      const bodyWidth: number = document.body.offsetWidth;
      const dropdownToggleSpaceToRight: number = this.componentRef.dropdownToggle.nativeElement.getBoundingClientRect().right;
      const dropdownToggleWidth: number = this.componentRef.dropdownToggle.nativeElement.offsetWidth;
      const userMenuDropdownWidth: number = this.componentRef.userMenuDropdown.nativeElement.offsetWidth;
      if (bodyWidth - dropdownToggleSpaceToRight + dropdownToggleWidth / 2 < userMenuDropdownWidth / 2) {
        this.renderer.addClass(this.componentRef.userMenuDropdown.nativeElement, 'ngbdropdown-tablet-adjustments');
      }
      // unsubscribe after user menu have been positioned
      if (this.dropdownMenuSubscription && !this.dropdownMenuSubscription.closed) {
        this.dropdownMenuSubscription.unsubscribe();
      }
    }
  }

  removeUserMenuTabletAdjustments(): void {
    if (this.componentRef.userMenuDropdown && this.componentRef.userMenuDropdown.nativeElement.classList.contains('ngbdropdown-tablet-adjustments')) {
      this.renderer.removeClass(this.componentRef.userMenuDropdown.nativeElement, 'ngbdropdown-tablet-adjustments');
    }
  }

  dropdownMenuMutationObserve(): void {
    // Target element
    const target: Element = this.componentRef.dropdownToggle.nativeElement.parentNode;
    // Configure observer
    const config: MutationObserverInit = {childList: true};
    const mutationObserverCallback: (mutationsList: MutationRecord[], observer: MutationObserver) => void =
      (mutationsList: MutationRecord[]): void => {
        for (const mutation of mutationsList) {
          if (mutation.addedNodes) {
            this.dropdownMenuOpened.emit(true);
          }
        }
      };
    // Create instance of observer with callback
    const observer: MutationObserver = new MutationObserver(mutationObserverCallback);
    // Start observe
    observer.observe(target, config);
  }

  userHeaderMutationObserve(): void {
    if (isPlatformBrowser(this.platformId)) {
      const target: Element = document.querySelector('.header');
      const config: MutationObserverInit = {childList: true};
      const mutationObserverCallback: (mutationsList: MutationRecord[], observer: MutationObserver) => void =
        (): void => {
          this.headerRebuild.emit(true);
        };
      const observer: MutationObserver = new MutationObserver(mutationObserverCallback);
      observer.observe(target, config);
    }
  }

  checkMediaView(width: number): void {
    if (width <= (CommerceConfig as BreakpointsConfig).breakpoints.xs) {
      this.mediaView = MediaView.MobileView;
    }
    if (width > (CommerceConfig as BreakpointsConfig).breakpoints.xs
      && width <= (CommerceConfig as BreakpointsConfig).breakpoints.md) {
      this.mediaView = MediaView.TabletView;
    }
    if (width > (CommerceConfig as BreakpointsConfig).breakpoints.md) {
      this.mediaView = MediaView.DesktopView;
    }
  }
}
